'use client';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Button } from '@startengine/design-system.ui.button';
import { ChevronDownIcon } from '@startengine/design-system.ui.icons';
import { V2_TYPOGRAPHY } from 'components/Common/HeroSectionV2/tokens';

export const CircleGraphImgBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '43%',
  aspectRatio: '1',
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    width: '15%',
  },
}));

export const HowToInvestBox = styled(Box)(() => ({
  display: 'flex',
  marginTop: '30px',
  position: 'relative',
  width: '100%',
}));

export const StepsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

export const BannerBox = styled(Box)(({ theme }) => ({
  aspectRatio: '326/414',
  display: 'flex',
  flexDirection: 'column',
  height: '416px',
  width: 'auto',
  position: 'relative',
  '> span': {
    position: 'initial !important',
  },
  '> span > img': {
    height: 'auto !important',
    position: 'initial !important',
  },
  [theme.breakpoints.up('sm')]: {
    aspectRatio: 'auto',
    height: '554px',
    width: '629.38px',
    '> span': {
      aspectRatio: 'auto',
    },
  },
}));

export const BannerTextBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  left: '20px',
  position: 'absolute',
  top: '15%',
  [theme.breakpoints.up('sm')]: {
    left: '12px',
    top: '7%',
  },
  [theme.breakpoints.up('md')]: {
    left: '42px',
    top: '90px',
    width: 'auto',
  },
}));

export const FooterBox = styled(Box)(({ theme }) => ({
  background: theme.palette.formField.backgroundColor,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '1140px',
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    paddingTop: '70px',
  },
}));

export const FooterSectionBox = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  background: theme.palette.formField.backgroundColor,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    margin: 0,
  },
}));

export const HelpSectionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '0 24px',
  marginTop: '40px',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
    margin: 0,
    padding: 0,
    width: '45%',
    marginBottom: '100px',
  },
}));

export const FooterImageBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '26px',
  width: '50%',
  position: 'relative',
  aspectRatio: '1',
  [theme.breakpoints.up('sm')]: {
    marginTop: '40px',
    width: '50%',
    height: '565px',
    aspectRatio: 'auto',
  },
}));

export const DividerBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.up('sm')]: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    '> svg': {
      width: '95%',
    },
  },
}));

export const BannerSectionBox = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '1280px',
  marginBottom: '56px',
  width: '100%',
  gap: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    margin: '160px auto 76px auto',
    gap: 0,
  },
}));

export const PromoBannerBg = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  background: theme.palette.common.dark,
  opacity: 0.9,
  display: 'flex',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  padding: '0 16px',
}));

export const PromoBarBox = styled(Box)(() => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  height: '50px',
  left: 0,
  position: 'fixed',
  right: 0,
  top: 0,
  zIndex: 1101, // Above Top Nav - banner should be visible at top
}));

export const PreviewBarBox = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.common.semicircle,
  display: 'flex',
  justifyContent: 'center',
  height: '50px',
  width: '200px',
  left: 0,
  position: 'fixed',
  right: 0,
  bottom: 0,
  zIndex: 1101, // Above Top Nav
}));

export const PromoTypography = styled(Typography)(({ theme }) => ({
  fontFamily: V2_TYPOGRAPHY.fontBody,
  color: '#FDFDFB',
  fontSize: '14px',
  lineHeight: '20px',
  // Don't auto-wrap on whitespace; only break where the CMS copy contains an
  // explicit newline. Lets marketing control line breaks from Strapi
  // (Hannah, 2026-06-03 — Slack #se-platform).
  whiteSpace: 'pre',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    lineHeight: '22px',
  },
}));

export const ButtonText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  lineHeight: '23px',
  [theme.breakpoints.up('sm')]: {
    fontWeight: '600',
    fontSize: '16px',
    lineHeight: '23px',
  },
}));

export const BannerButtonText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  lineHeight: '23px',
  [theme.breakpoints.up('sm')]: {
    fontWeight: '600',
    fontSize: '8px',
    lineHeight: '12px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    lineHeight: '23px',
  },
}));

export const MobileBannerButtonCopy = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.black,
  fontWeight: '700',
  fontSize: '14px',
  lineHeight: '20px',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

export const DesktopBannerButtonCopy = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.black,
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    fontWeight: '700',
    fontSize: '14px',
    lineHeight: '20px',
    display: 'block',
  },
}));

export const HowToInvestTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.dark,
  bottom: 0,
  lineHeight: '56px',
  maxWidth: '80%',
  position: 'absolute',
  [theme.breakpoints.up('sm')]: {
    fontWeight: '400',
    fontSize: '42px',
    lineHeight: '42px',
    letterSpacing: '0.06em',
    left: '50px',
    marginBottom: '20px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '56px',
    lineHeight: '56px',
  },
}));

export const StepNumberTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.dark,
  letterSpacing: '2px',
  [theme.breakpoints.between('sm', 'md')]: {
    fontWeight: '400',
    fontSize: '16px',
    lineHeight: '21px',
  },
}));

export const StepTitleTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.dark,
  letterSpacing: '2px',
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '18px',
    lineHeight: '24px',
    maxWidth: '70%',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '75%',
  },
}));

export const StepSubtitleTypography = styled(Typography)(({ theme }) => ({
  color: '#767676',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
    fontSize: '12px',
    lineHeight: '16px',
    maxWidth: '76%',
  },
  [theme.breakpoints.up('md')]: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
  },
}));

export const BannerTitleTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.midnight,
  fontSize: '32px',
  fontWeight: 400,
  lineHeight: '36px',
  letterSpacing: '0.06em',
  [theme.breakpoints.up('sm')]: {
    fontSize: '36px',
    lineHeight: '44px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '48px',
    lineHeight: '61px',
  },
}));

export const BannerSubtitleTypography = styled(Typography)(
  ({ theme, desktopwidth, mobileWidth = '0%' }) => ({
    color: theme.palette.common.black,
    width: mobileWidth !== '0%' ? mobileWidth : 'auto',
    [theme.breakpoints.up('sm')]: {
      fontSize: '10px',
      lineHeight: '17px',
      width: desktopwidth,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '16px',
      lineHeight: '22px',
      maxWidth: '290px',
    },
  }),
);

export const BannerActionTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  '& a': {
    fontWeight: 600,
    textDecoration: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
}));

export const HelpTitleTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.midnight,
  fontSize: '42px',
  fontWeight: 500,
  lineHeight: '56px',
  letterSpacing: '0.06em',
  padding: '0 16px',
  [theme.breakpoints.up('md')]: {
    fontSize: '56px',
    lineHeight: '74px',
  },
}));

export const HelpSubtitleTypography = styled(Typography)(({ theme }) => ({
  color: '#767676',
  fontSize: '18px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.05px',
  padding: '0 16px',
  [theme.breakpoints.up('md')]: {
    fontSize: '24px',
    lineHeight: '31px',
    marginTop: '4px',
  },
}));

export const GreenATag = styled('span')(({ theme }) => ({
  color: theme.palette.secondary.main,
  cursor: 'pointer',
  fontWeight: '700',
}));

export const HeroButton = styled(Button)(({ theme }) => ({
  borderRadius: '16px',
  height: '48px',
  maxWidth: '413px',
  whiteSpace: 'nowrap',
  [theme.breakpoints.between('sm', 'md')]: {
    borderRadius: '12px',
  },
  [theme.breakpoints.up('md')]: {},
  [theme.breakpoints.up('lg')]: {},
}));

export const PromoButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '10px',
  border: 0,
  height: '30px',
  marginLeft: '8px',
  whiteSpace: 'nowrap',
  '> span': {
    color: theme.palette.common.black,
  },
}));

export const BannerButton = styled(Button)(({ theme }) => ({
  borderRadius: '16px',
  bottom: '38px',
  height: '48px',
  left: '24px',
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '52%',
  [theme.breakpoints.between('sm', 'md')]: {
    borderRadius: '8px',
    height: '28px',
    width: '27%',
  },
  [theme.breakpoints.up('md')]: {
    width: '27%',
  },
}));

export const CarouselGrid = styled(Grid)(
  () => `
  > div{
    height: auto;
  }
  div.activeEvent {
    height: 219px;
    span {
      height: 100% !important;
    }
  }
  @media (min-width: 600px) {
    div.activeEvent {
      height: auto;
    }  
  }
  @media (max-width: 599px) {
    .outerTombstone:last-child {
      width: 90% !important;
      .MuiBox-root {
        padding-right: 18px !important;
      }
    }
  }
  `,
);

export const AccordionContainer = styled(Grid)(
  () => `
  .MuiAccordionSummary-content {
    margin: 24px 0;
  }
  .MuiTypography-root { 
    line-height: 22px;
  }
  `,
);

export const AccordionButton = styled(ChevronDownIcon)`
  height: 36px;
  width: 36px;
`;

export const V2HomepageMain = styled('section')({
  animation: 'v2FadeIn 0.3s ease-in-out',
  overflowX: 'hidden',
  width: '100%',
  maxWidth: '100%',
  '@keyframes v2FadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
});

export const BannerDisclaimerText = styled(Typography)(({ theme }) => ({
  fontFamily: "'IBM Plex Sans', sans-serif",
  fontSize: '8px',
  fontWeight: 400,
  lineHeight: '10px',
  letterSpacing: '0.05px',
  color: '#666666',
  fontStyle: 'italic',
  marginTop: '8px',
  maxWidth: '290px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '8px',
    lineHeight: '10px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '10px',
    lineHeight: '12px',
  },
}));
